import React, { useState, useEffect, useRef } from 'react';
import './PageSettings.css';
import { BiChevronDown, BiChevronRight, BiData, BiLinkAlt, BiCode, BiPlus, BiListUl, BiX } from 'react-icons/bi';

const PageSettings = ({ page, onUpdatePage, contentItems, selectedContentItem }) => {
  const [showGeneral, setShowGeneral] = useState(true);
  const [showSEO, setShowSEO] = useState(true);
  const [showTemplate, setShowTemplate] = useState(true);
  const [showCode, setShowCode] = useState(true);
  const [showFields, setShowFields] = useState(false);
  const [previewContentItem, setPreviewContentItem] = useState(selectedContentItem || null);
  const fieldButtonRefs = {
    urlPattern: useRef(null),
    title: useRef(null),
    description: useRef(null)
  };
  const [settings, setSettings] = useState({
    pageId: page?.id,
    name: page?.name || '',
    slug: page?.slug || '',
    seo: {
      title: page?.seo?.title || '',
      description: page?.seo?.description || ''
    },
    template: {
      urlPattern: page?.template?.urlPattern || '',
      primaryField: page?.template?.primaryField || '',
      contentTypeId: page?.contentTypeId || ''
    },
    customCode: {
      head: page?.customCode?.head || '',
      body: page?.customCode?.body || ''
    }
  });
  
  // Get the content type schema from contentItems
  const getContentTypeSchema = () => {
    if (!page?.contentTypeId || !contentItems || contentItems.length === 0) return null;
    
    // Find the first content item that matches the page's content type ID
    const matchingItem = contentItems.find(item => 
      item.contentType && item.contentType._id === page.contentTypeId
    );
    
    return matchingItem?.contentType || null;
  };
  
  const contentTypeSchema = getContentTypeSchema();
  
  // Get available fields from the content type schema
  const getAvailableFields = () => {
    if (!contentTypeSchema) return [];
    
    // Extract fields from the schema
    const fields = contentTypeSchema.fields || [];
    return fields.map(field => ({
      name: field.name,
      label: field.label || field.name,
      type: field.type
    }));
  };
  
  const availableFields = getAvailableFields();

  // Parse field references from text
  const parseFieldReferences = (text) => {
    if (!text) return [];
    
    const fieldRefs = [];
    const regex = /\[field:([^\]]+)\]/g;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      fieldRefs.push({
        full: match[0],
        name: match[1],
        index: match.index
      });
    }
    
    return fieldRefs;
  };

  // Process dynamic field placeholders in a string with actual content values
  const processDynamicFields = (text, contentItem) => {
    if (!text || !contentItem) return text;
    
    // Replace [field:name] with actual content values
    return text.replace(/\[field:([^\]]+)\]/g, (match, fieldName) => {
      return contentItem.data[fieldName] || match;
    });
  };

  // Insert field reference into input at cursor position
  const insertFieldReference = (fieldName, inputType) => {
    // Determine which field to update
    let fieldValue = '';
    
    if (inputType === 'urlPattern') {
      fieldValue = settings.template?.urlPattern || '';
    } else if (inputType === 'title') {
      fieldValue = settings.seo?.title || '';
    } else if (inputType === 'description') {
      fieldValue = settings.seo?.description || '';
    }
    
    // Insert field reference
    const fieldReference = `[field:${fieldName}]`;
    const newValue = fieldValue + fieldReference;
    
    // Update the appropriate section
    if (inputType === 'urlPattern') {
      handleChange('template', 'urlPattern', newValue);
    } else if (inputType === 'title') {
      handleChange('seo', 'title', newValue);
    } else if (inputType === 'description') {
      handleChange('seo', 'description', newValue);
    }
    
    // Close the fields panel
    setShowFields(false);
  };

  // Remove field reference from input
  const removeFieldReference = (fieldRef, inputType) => {
    // Determine which field to update
    let fieldValue, updateFunction;
    
    if (inputType === 'urlPattern') {
      fieldValue = settings.template.urlPattern;
      updateFunction = (newValue) => handleChange('template', 'urlPattern', newValue);
    } else if (inputType === 'title') {
      fieldValue = settings.seo.title;
      updateFunction = (newValue) => handleChange('seo', 'title', newValue);
    } else if (inputType === 'description') {
      fieldValue = settings.seo.description;
      updateFunction = (newValue) => handleChange('seo', 'description', newValue);
    }
    
    // Remove the field reference
    const newValue = fieldValue.replace(fieldRef.full, '');
    updateFunction(newValue);
  };

  // Toggle field selector dropdown
  const toggleFieldSelector = (fieldType) => {
    if (showFields === fieldType) {
      setShowFields(false);
    } else {
      setShowFields(fieldType);
    }
  };

  // Update initialization effect to properly handle template data
  useEffect(() => {
    console.log('Page changed:', page); // Debug log
    if (page) {
      const templateSettings = {
        urlPattern: page.template?.urlPattern || '',
        primaryField: page.template?.primaryField || '',
        contentTypeId: page.contentTypeId || ''
      };

      const initialSettings = {
        pageId: page.id,
        name: page.name || '',
        slug: page.slug || '',
        seo: {
          title: page.seo?.title || '',
          description: page.seo?.description || ''
        },
        template: templateSettings,
        customCode: {
          head: page.customCode?.head || '',
          body: page.customCode?.body || ''
        }
      };

      console.log('Initializing settings with template:', initialSettings); // Debug log
      setSettings(initialSettings);
    }
  }, [page?.id, page?.template?.urlPattern]); // Add urlPattern to dependencies

  // Separate effect for preview content item
  useEffect(() => {
    setPreviewContentItem(selectedContentItem || null);
  }, [selectedContentItem]);

  // Close field selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFields) {
        const fieldButton = fieldButtonRefs[showFields]?.current;
        const fieldSelector = document.querySelector('.field-selector-dropdown');
        
        if (fieldButton && !fieldButton.contains(event.target) && 
            fieldSelector && !fieldSelector.contains(event.target)) {
          setShowFields(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFields]);

  const handleChange = (section, field, value) => {
    console.log('Handling change:', { section, field, value }); // Debug log
    
    const newSettings = JSON.parse(JSON.stringify(settings)); // Deep clone to avoid reference issues
    
    if (section === 'template') {
      // For template changes, only update the specific field
      newSettings.template = {
        ...settings.template, // Keep existing template settings
        [field]: value, // Update only the specific field
        contentTypeId: page?.contentTypeId || '' // Ensure contentTypeId is preserved
      };
    } else if (section === 'seo') {
      newSettings.seo = {
        ...settings.seo,
        [field]: value
      };
    } else if (section === 'customCode') {
      newSettings.customCode = {
        ...settings.customCode,
        [field]: value
      };
    } else {
      newSettings[section] = value;
    }

    // Auto-generate slug for non-template pages
    if (section === 'name' && !page?.isTemplate) {
      const currentSlug = settings.slug;
      if (!currentSlug) {
        const generatedSlug = value.toLowerCase()
          .replace(/[^a-z0-9-\s]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');

        newSettings.slug = generatedSlug;
      }
    }

    // Update local state first
    setSettings(newSettings);
    
    // Create a complete settings object for the update
    const updatedSettings = {
      ...newSettings,
      id: page.id,
      isTemplate: page.isTemplate,
      contentTypeId: page.contentTypeId,
      template: {
        ...newSettings.template,
        urlPattern: newSettings.template?.urlPattern || '',
        primaryField: newSettings.template?.primaryField || '',
        contentTypeId: page?.contentTypeId || ''
      },
      customCode: {
        ...newSettings.customCode
      }
    };
    
    // Log the complete update object
    console.log('Sending complete update:', updatedSettings);
    
    // Send the update to parent
    onUpdatePage(updatedSettings);
  };

  const getSearchPreview = () => {
    let title = settings.seo.title || settings.name;
    let description = settings.seo.description || 'No description provided...';
    let url = '';
    
    if (page?.isTemplate) {
      // For template pages, process dynamic fields if a preview content item is selected
      if (previewContentItem) {
        title = processDynamicFields(title, previewContentItem);
        description = processDynamicFields(description, previewContentItem);
        
        // Process URL pattern with dynamic fields
        const pattern = settings.template.urlPattern || '';
        url = `yourdomain.com/${processDynamicFields(pattern, previewContentItem)}`;
      } else {
        url = `yourdomain.com/${settings.template.urlPattern || '[dynamic-url]'}`;
      }
    } else {
      url = `yourdomain.com/${settings.slug || ''}`;
    }
      
    return (
      <div className="search-preview">
        <div className="preview-title">{title}</div>
        <div className="preview-url">{url}</div>
        <div className="preview-description">{description}</div>
      </div>
    );
  };

  // Render field chips for a given input
  const renderFieldChips = (inputType) => {
    let value = '';
    
    if (inputType === 'urlPattern') {
      value = settings.template.urlPattern;
    } else if (inputType === 'title') {
      value = settings.seo.title;
    } else if (inputType === 'description') {
      value = settings.seo.description;
    }
    
    const fieldRefs = parseFieldReferences(value);
    
    if (fieldRefs.length === 0) return null;
    
    return (
      <div className="field-chips">
        {fieldRefs.map((fieldRef, index) => (
          <div key={index} className="field-chip">
            <span className="field-chip-name">{fieldRef.name}</span>
            <button 
              className="field-chip-remove" 
              onClick={() => removeFieldReference(fieldRef, inputType)}
              title="Remove field"
            >
              <BiX />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const getSlugPreview = () => {
    const baseUrl = 'yourdomain.com/';
    const slug = settings.slug || '';
    
    return (
      <div className="slug-preview">
        <div className="slug-preview-parts">
          <span className="base-url">{baseUrl}</span>
          <span className="slug-part">{slug || 'page-url-slug'}</span>
        </div>
        <small>
          {slug ? (
            <span className="valid-slug">✓ Valid URL format</span>
          ) : (
            <span className="warning-slug">! Add a URL slug</span>
          )}
        </small>
      </div>
    );
  };

  const getUrlPatternPreview = () => {
    const baseUrl = 'yourdomain.com/';
    const pattern = settings.template.urlPattern || '';
    
    return (
      <div className="slug-preview">
        <div className="slug-preview-parts">
          <span className="base-url">{baseUrl}</span>
          <span className="slug-part">{pattern || '[content-type]/[id-or-slug]'}</span>
        </div>
        <small>
          {pattern ? (
            <span className="valid-slug">✓ Valid URL pattern</span>
          ) : (
            <span className="warning-slug">! Add a URL pattern</span>
          )}
        </small>
        <div className="pattern-help">
          <small>Use <code>[field:name]</code> to include dynamic content fields in the URL</small>
        </div>
      </div>
    );
  };

  // Handle content item selection for preview
  const handlePreviewContentSelect = (contentItemId) => {
    if (!contentItemId) {
      setPreviewContentItem(null);
      return;
    }
    
    const selectedItem = contentItems.find(item => item._id === contentItemId);
    setPreviewContentItem(selectedItem);
  };

  return (
    <div className="page-settings">
      <div className="page-settings-header">
        <h3>Page Settings</h3>
        {page?.isTemplate && <span className="template-badge"><BiData /> Template</span>}
      </div>

      <div className="settings-sections">
        {/* General Settings */}
        <div className="settings-section">
          <div 
            className="settings-category-header"
            onClick={() => setShowGeneral(!showGeneral)}
          >
            {showGeneral ? <BiChevronDown /> : <BiChevronRight />}
            <span>General</span>
          </div>
          
          {showGeneral && (
            <div className="settings-category-content">
              <div className="form-group">
                <label>Page Name</label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => handleChange('name', null, e.target.value)}
                  placeholder="Enter page name"
                />
              </div>
              
              {!page?.isTemplate ? (
                <div className="form-group">
                  <label>URL Slug</label>
                  <input
                    type="text"
                    value={settings.slug}
                    onChange={(e) => {
                      const formattedSlug = e.target.value.toLowerCase()
                        .replace(/[^a-z0-9-]/g, '-')
                        .replace(/-+/g, '-')
                        .replace(/^-|-$/g, '');
                      handleChange('slug', null, formattedSlug);
                    }}
                    placeholder="enter-url-slug"
                  />
                  {getSlugPreview()}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Template Settings - Only show for template pages */}
        {page?.isTemplate && (
          <div className="settings-section">
            <div 
              className="settings-category-header"
              onClick={() => setShowTemplate(!showTemplate)}
            >
              {showTemplate ? <BiChevronDown /> : <BiChevronRight />}
              <span>Template Settings</span>
            </div>
            
            {showTemplate && (
              <div className="settings-category-content">
                <div className="form-group">
                  <label>Content Type</label>
                  <div className="content-type-display">
                    <BiData className="content-type-icon" />
                    <span>{contentTypeSchema?.name || page.contentTypeName || "Unknown Content Type"}</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <div className="field-group-header">
                    <label>URL Pattern</label>
                    <div className="field-dropdown-container">
                      <button 
                        ref={fieldButtonRefs.urlPattern}
                        className={`field-add-button ${showFields === 'urlPattern' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleFieldSelector('urlPattern');
                        }}
                        title="Add field"
                      >
                        <BiPlus /> Add Field
                      </button>
                      
                      {showFields === 'urlPattern' && (
                        <div className="field-selector-dropdown">
                          <div className="field-selector-header">
                            <h4>Insert Content Field</h4>
                          </div>
                          <div className="field-selector-content">
                            {availableFields.length > 0 ? (
                              <div className="field-list">
                                {availableFields.map(field => (
                                  <div 
                                    key={field.name} 
                                    className="field-item"
                                    onClick={() => insertFieldReference(field.name, 'urlPattern')}
                                  >
                                    <span className="field-name">{field.label}</span>
                                    <span className="field-type">{field.type}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-fields-message">
                                No fields available for this content type.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <input
                    type="text"
                    value={settings.template.urlPattern || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      handleChange('template', 'urlPattern', newValue);
                    }}
                    placeholder="blog/[field:slug]"
                  />
                  
                  {renderFieldChips('urlPattern')}
                  {getUrlPatternPreview()}
                </div>
                
                {contentItems && contentItems.length > 0 && (
                  <div className="form-group">
                    <label>Preview with Content</label>
                    <select 
                      className="preview-content-select"
                      value={previewContentItem?._id || ''}
                      onChange={(e) => handlePreviewContentSelect(e.target.value)}
                    >
                      <option value="">Select content to preview</option>
                      {contentItems
                        .filter(item => item.contentType && item.contentType._id === page.contentTypeId)
                        .map(item => {
                          // Get the first available field value for display
                          const displayValue = item.data[Object.keys(item.data)[0]] || 'Untitled';
                          return (
                            <option key={item._id} value={item._id}>
                              {displayValue}
                            </option>
                          );
                        })}
                    </select>
                    {previewContentItem && (
                      <div className="preview-content-info">
                        <small>Previewing with: <strong>{previewContentItem.data[Object.keys(previewContentItem.data)[0]] || 'Untitled'}</strong></small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SEO Settings */}
        <div className="settings-section">
          <div 
            className="settings-category-header"
            onClick={() => setShowSEO(!showSEO)}
          >
            {showSEO ? <BiChevronDown /> : <BiChevronRight />}
            <span>SEO Settings</span>
          </div>
          
          {showSEO && (
            <div className="settings-category-content">
              <div className="form-group">
                <div className="field-group-header">
                  <label>Title Tag</label>
                  {page?.isTemplate && (
                    <div className="field-dropdown-container">
                      <button 
                        ref={fieldButtonRefs.title}
                        className={`field-add-button ${showFields === 'title' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleFieldSelector('title');
                        }}
                        title="Add field"
                      >
                        <BiPlus /> Add Field
                      </button>
                      
                      {showFields === 'title' && (
                        <div className="field-selector-dropdown">
                          <div className="field-selector-header">
                            <h4>Insert Content Field</h4>
                          </div>
                          <div className="field-selector-content">
                            {availableFields.length > 0 ? (
                              <div className="field-list">
                                {availableFields.map(field => (
                                  <div 
                                    key={field.name} 
                                    className="field-item"
                                    onClick={() => insertFieldReference(field.name, 'title')}
                                  >
                                    <span className="field-name">{field.label}</span>
                                    <span className="field-type">{field.type}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-fields-message">
                                No fields available for this content type.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <input
                  type="text"
                  value={settings.seo.title}
                  onChange={(e) => handleChange('seo', 'title', e.target.value)}
                  placeholder={page?.isTemplate ? "[field:title] - Your Site" : "Enter title tag"}
                />
                
                {page?.isTemplate && renderFieldChips('title')}
                <small>Recommended length: 50-60 characters</small>
              </div>
              
              <div className="form-group">
                <div className="field-group-header">
                  <label>Meta Description</label>
                  {page?.isTemplate && (
                    <div className="field-dropdown-container">
                      <button 
                        ref={fieldButtonRefs.description}
                        className={`field-add-button ${showFields === 'description' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleFieldSelector('description');
                        }}
                        title="Add field"
                      >
                        <BiPlus /> Add Field
                      </button>
                      
                      {showFields === 'description' && (
                        <div className="field-selector-dropdown">
                          <div className="field-selector-header">
                            <h4>Insert Content Field</h4>
                          </div>
                          <div className="field-selector-content">
                            {availableFields.length > 0 ? (
                              <div className="field-list">
                                {availableFields.map(field => (
                                  <div 
                                    key={field.name} 
                                    className="field-item"
                                    onClick={() => insertFieldReference(field.name, 'description')}
                                  >
                                    <span className="field-name">{field.label}</span>
                                    <span className="field-type">{field.type}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-fields-message">
                                No fields available for this content type.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <textarea
                  value={settings.seo.description}
                  onChange={(e) => handleChange('seo', 'description', e.target.value)}
                  placeholder={page?.isTemplate ? "Read about [field:title]..." : "Enter meta description"}
                  rows="3"
                />
                
                {page?.isTemplate && renderFieldChips('description')}
                <small>Recommended length: 150-160 characters</small>
              </div>
              
              <div className="form-group">
                <label>Search Result Preview</label>
                {getSearchPreview()}
              </div>
            </div>
          )}
        </div>

        {/* Custom Code Settings */}
        <div className="settings-section">
          <div 
            className="settings-category-header"
            onClick={() => setShowCode(!showCode)}
          >
            {showCode ? <BiChevronDown /> : <BiChevronRight />}
            <span><BiCode /> Custom Code</span>
          </div>
          
          {showCode && (
            <div className="settings-category-content">
              <div className="custom-code-disclaimer">
                <small className="disclaimer-text">
                  ⓘ Custom code will only take effect when your site is published. Changes made here won't be visible in the editor preview.
                </small>
              </div>
              <div className="form-group">
                <label>Head Code</label>
                <textarea
                  className="code-textarea"
                  value={settings.customCode.head}
                  onChange={(e) => handleChange('customCode', 'head', e.target.value)}
                  placeholder="<!-- Add custom code to the <head> section -->"
                  rows="5"
                />
                <small>Custom code added to the &lt;head&gt; tag (scripts, meta tags, styles, etc.)</small>
              </div>
              
              <div className="form-group">
                <label>Body Code</label>
                <textarea
                  className="code-textarea"
                  value={settings.customCode.body}
                  onChange={(e) => handleChange('customCode', 'body', e.target.value)}
                  placeholder="<!-- Add custom code to the end of the <body> section -->"
                  rows="5"
                />
                <small>Custom code added to the end of the &lt;body&gt; tag (scripts, tracking codes, etc.)</small>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageSettings; 